import { PrayerStatuses, Unit } from "@/utils/enums";
import { AuthUser } from "@/features/auth";

// Determine prayer status based on user's unit and role
export const getPrayerStatusByUserRole = (user: AuthUser) => {
    const userUnit = user.currentRole.unit;
    const userRole = user.currentRole.role.name;

    // Check specific role-based statuses first
    if (userRole === "SBC_CHAIRMAN") {
        return PrayerStatuses.Recommended; // SBC chairman recommends prayers
    }
    if (userRole === "SENATE_CHAIRMAN") {
        return PrayerStatuses.Approved; // Senate chairman approves prayers
    }
    if (userRole === "DEAN") {
        return PrayerStatuses.Submitted; // Dean submits prayers
    }
    if (userRole === "HOD") {
        return PrayerStatuses.Raised; // HOD raises prayers
    }
    if (userRole === "DEPARTMENTAL_EXAMS_OFFICER") {
        return PrayerStatuses.Initiated; // Departmental exams officer initiates prayers
    }

    // Fallback to unit-based statuses
    switch (userUnit) {
        case Unit.Department:
            return PrayerStatuses.Initiated;
        case Unit.Faculty:
            return PrayerStatuses.Raised;
        case Unit.Sbc:
            return PrayerStatuses.Submitted;
        case Unit.Senate:
            return PrayerStatuses.Submitted;
        case Unit.Secretariat:
            return PrayerStatuses.Recommended;
        default:
            return PrayerStatuses.Initiated;
    }
};

// Check if user can create prayers based on their role and privileges
export const canCreatePrayer = (user: AuthUser, hasInitiatePrivilege: boolean) => {
    // Check if user has the basic privilege to initiate prayers
    if (!hasInitiatePrivilege) {
        return false;
    }

    // Additional role-based checks
    const userRole = user.currentRole.role.name;
    const userUnit = user.currentRole.unit;

    // Specific role checks
    if (userRole === "DEPARTMENTAL_EXAMS_OFFICER" || userRole === "HOD") {
        return true; // Can always create prayers
    }
    if (userRole === "DEAN") {
        return true; // Can create prayers
    }
    if (userRole === "SBC_CHAIRMAN") {
        return true; // Can create prayers
    }
    if (userRole === "SENATE_CHAIRMAN") {
        return true; // Can create prayers
    }

    // Unit-based checks
    return [Unit.Department, Unit.Faculty, Unit.Sbc, Unit.Senate, Unit.Secretariat].includes(
        userUnit as Unit
    );
};

// Determine what actions user can perform on prayers based on their role
export const getPrayerActions = (user: AuthUser) => {
    const userRole = user.currentRole.role.name;
    const userUnit = user.currentRole.unit;

    const actions = {
        canInitiate: false,
        canRaise: false,
        canSubmit: false,
        canRecommend: false,
        canApprove: false,
        canReject: false,
        canStepDown: false,
        canViewAll: false,
        canViewFaculty: false,
        canViewDepartment: false,
    };

    // Role-based permissions
    switch (userRole) {
        case "DEPARTMENTAL_EXAMS_OFFICER":
            actions.canInitiate = true;
            actions.canViewDepartment = true;
            break;
        case "HOD":
            actions.canInitiate = true;
            actions.canRaise = true;
            actions.canViewDepartment = true;
            break;
        case "DEAN":
            actions.canInitiate = true;
            actions.canRaise = true;
            actions.canSubmit = true;
            actions.canViewFaculty = true;
            break;
        case "SBC_CHAIRMAN":
            actions.canInitiate = true;
            actions.canRecommend = true;
            actions.canStepDown = true;
            actions.canViewAll = true;
            break;
        case "SENATE_CHAIRMAN":
            actions.canInitiate = true;
            actions.canApprove = true;
            actions.canReject = true;
            actions.canViewAll = true;
            break;
        case "SENATOR":
            actions.canViewAll = true;
            break;
        default:
            // Unit-based fallback
            switch (userUnit) {
                case Unit.Department:
                    actions.canInitiate = true;
                    actions.canViewDepartment = true;
                    break;
                case Unit.Faculty:
                    actions.canInitiate = true;
                    actions.canRaise = true;
                    actions.canViewFaculty = true;
                    break;
                case Unit.Sbc:
                    actions.canInitiate = true;
                    actions.canViewAll = true;
                    break;
                case Unit.Senate:
                    actions.canInitiate = true;
                    actions.canViewAll = true;
                    break;
                case Unit.Secretariat:
                    actions.canInitiate = true;
                    actions.canViewAll = true;
                    break;
            }
    }

    return actions;
};
